import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Loader2, Lock, CreditCard, Heart, PartyPopper, Clock3 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [smsTime, setSmsTime] = useState('08:00');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const smsTimeSlots = Array.from({ length: 60 }, (_, index) => {
    const totalMinutes = 8 * 60 + index * 15;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }).filter((time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const total = hours * 60 + minutes;
    return total >= 8 * 60 && total <= 22 * 60 + 45;
  });

  const handleCheckout = async () => {
    if (!email || !phone1 || !phone2) {
      toast.error('Vyplňte prosím všechna pole.');
      return;
    }

    if (!agreedToTerms) {
      toast.error('Musíte souhlasit s obchodními podmínkami.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_type: 'partnerska',
          package_size: 'standard',
          email1: email,
          phone1: phone1,
          phone2: phone2,
          sms_time: smsTime,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        const message =
          data?.error ||
          data?.detail ||
          (Array.isArray(data?.issues) && data.issues.length
            ? data.issues.map((i) => i.message).join(', ')
            : 'Nepodařilo se vytvořit platbu.');

        toast.error(message);
      }
    } catch (error) {
      toast.error('Nepodařilo se vytvořit platbu. Zkuste to znovu.');
    } finally {
      setIsLoading(false);
    }
  };

  const parterskaFeatures = [
    'Digitální forma – SMS každý den',
    'Pro 2 osoby',
    'Jedna výzva pro oba',
    '14 dní výzev',
    'Okamžité doručení',
  ];

  const rozluckovaFeatures = [
    '10–30 úkolů',
    'PDF ke čtení i tisku',
    'Možnost certifikátu',
    'Personalizace',
    'Unikátní obsah',
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="py-20 section-warm">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-semibold text-[var(--vp-ink)] mb-4">
            Vyberte si svou výzvu
          </h2>
          <p className="text-[var(--vp-ink)]/70 max-w-2xl mx-auto">
            Zvolte variantu, která vám dává smysl.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Partnerska Vyzva Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="pricing-partnerska-card"
            className="bg-white rounded-3xl p-8 border-2 border-[var(--vp-pink)] shadow-[var(--shadow-lift)] relative"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-[var(--vp-pink)] text-white text-sm font-medium px-4 py-1 rounded-full">
                Doporučeno
              </span>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--vp-pink)]/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[var(--vp-pink)]" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[var(--vp-ink)]">
                  Párová výzva
                </h3>
                <p className="text-sm text-[var(--vp-ink)]/60">
                  14 dní společných momentů
                </p>
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-6">
              <span
                className="text-4xl font-bold text-[var(--vp-ink)]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                379
              </span>
              <span className="text-lg text-[var(--vp-ink)]/70">Kč</span>
            </div>

            <ul className="space-y-3 mb-8">
              {parterskaFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-[var(--vp-ink)]">
                  <Check className="w-5 h-5 text-[var(--vp-mint)]" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="space-y-4 border-t border-[var(--vp-line)] pt-6">
              <div>
                <label className="block text-sm font-medium text-[var(--vp-ink)] mb-1">
                  Váš e-mail
                </label>
                <Input
                  type="email"
                  placeholder="vas@email.cz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="pricing-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-[var(--vp-ink)] mb-1">
                    Telefon 1
                  </label>
                  <Input
                    type="tel"
                    placeholder="+420..."
                    value={phone1}
                    onChange={(e) => setPhone1(e.target.value)}
                    data-testid="pricing-phone1-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--vp-ink)] mb-1">
                    Telefon 2
                  </label>
                  <Input
                    type="tel"
                    placeholder="+420..."
                    value={phone2}
                    onChange={(e) => setPhone2(e.target.value)}
                    data-testid="pricing-phone2-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="sms-time" className="block text-sm font-medium text-[var(--vp-ink)] mb-1">
                  Čas SMS
                </label>
                <div className="relative">
                  <Clock3 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vp-ink)]/50" />
                  <select
                    id="sms-time"
                    name="sms_time"
                    value={smsTime}
                    onChange={(e) => setSmsTime(e.target.value)}
                    data-testid="pricing-sms-time-select"
                    className="w-full appearance-none rounded-xl border border-[var(--vp-line)] bg-white px-10 py-3 text-[var(--vp-ink)] shadow-sm focus:border-[var(--vp-pink)] focus:outline-none focus:ring-2 focus:ring-[var(--vp-pink)]/20"
                  >
                    {smsTimeSlots.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[var(--vp-line)] text-[var(--vp-pink)] focus:ring-[var(--vp-pink)]"
                  data-testid="pricing-terms-checkbox"
                />
                <span className="text-sm text-[var(--vp-ink)]/70">
                  Souhlasím s{' '}
                  <a href="/obchodni-podminky.html" target="_blank" rel="noreferrer" className="text-[var(--vp-pink)] hover:underline">
                    obchodními podmínkami
                  </a>{' '}
                  a beru na vědomí{' '}
                  <a href="/gdpr.html" target="_blank" rel="noreferrer" className="text-[var(--vp-pink)] hover:underline">
                    zásady ochrany osobních údajů
                  </a>.
                </span>
              </label>

              <Button
                onClick={handleCheckout}
                disabled={isLoading}
                data-testid="pricing-partnerska-checkout-button"
                className="w-full btn-accent py-4 rounded-xl text-base font-semibold"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Zaplatit 379 Kč
                  </>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-[var(--vp-ink)]/50">
                <Lock className="w-3 h-3" />
                <span>Bezpečná platba přes Stripe</span>
                <CreditCard className="w-3 h-3 ml-2" />
              </div>
            </div>
          </motion.div>

          {/* Rozluckova Vyzva Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="pricing-rozluckova-card"
            className="bg-white rounded-3xl p-8 border border-[var(--vp-line)] shadow-[var(--shadow-soft)] relative opacity-80"
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-[var(--vp-mint)] text-[var(--vp-ink)] text-sm font-medium px-4 py-1 rounded-full">
                Připravujeme
              </span>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[var(--vp-mint)]/10 flex items-center justify-center">
                <PartyPopper className="w-6 h-6 text-[var(--vp-ink)]/60" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[var(--vp-ink)]">
                  Rozlučková výzva
                </h3>
                <p className="text-sm text-[var(--vp-ink)]/60">
                  Na míru pro vaši rozlučku
                </p>
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-lg text-[var(--vp-ink)]/70">od</span>
              <span
                className="text-4xl font-bold text-[var(--vp-ink)]/60"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                179
              </span>
              <span className="text-lg text-[var(--vp-ink)]/70">Kč</span>
            </div>

            <ul className="space-y-3 mb-8">
              {rozluckovaFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-[var(--vp-ink)]/60">
                  <Check className="w-5 h-5 text-[var(--vp-line)]" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              disabled
              className="w-full py-4 rounded-xl text-base bg-[var(--vp-line)] text-[var(--vp-ink)]/40 cursor-not-allowed"
            >
              Brzy k dispozici
            </Button>
          </motion.div>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center text-sm text-[var(--vp-ink)]/50 mt-8 max-w-2xl mx-auto"
        >
          Výzva Přijata je digitální hra s inspiračním a zábavním obsahem.
          Nejedná se o terapii ani poradenskou službu.
        </motion.p>
      </div>
    </section>
  );
};

export default PricingSection;